// Règle de Benoit : une cliente ne doit JAMAIS voir, dans son app ou son PDF,
// qu'un contenu a été rédigé par un outil ou une IA. Ce garde lit tout le
// texte d'un lot, d'une recette ou du livrable final et signale les mentions
// d'outil et d'IA et les préambules typiques d'une réponse générée.
//
// Faux positifs évités : le prénom et le nom de la cliente sont retirés avant
// la lecture (« Bonjour Claude »), « Claude » seul n'est jamais lu (reine-
// claude, Saint-Claude), « GPT » seul non plus (GPT/ALAT, TGP/GPT 45 UI/L) :
// seuls les noms d'outil sans ambiguïté sont lus.
//
// Deux niveaux : « certain » (chatgpt, openai, gpt-4…, en tant qu'IA, je suis
// une IA, modèle de langage, as an AI) et « possible » (IA, intelligence
// artificielle, Claude AI, Anthropic, préambules). Les lots nouveaux bloquent
// sur les deux ; la publication bloque sur « certain » et signale « possible ».

export const AI_MENTION_MESSAGE = "Le texte mentionne un outil ou une IA : reformule avant d’enregistrer";

const L = "\\p{L}\\p{N}";
// Jamais collé à une lettre ou un chiffre (« l'IA », « d'OpenAI » restent lus).
const word = (body, flags = "iu") => new RegExp(`(?<![${L}])(?:${body})(?![${L}])`, flags);
// Claude : en plus, jamais après un trait d'union ou une apostrophe (reine-claude).
const strictWord = (body, flags = "iu") => new RegExp(`(?<![${L}'’-])(?:${body})(?![${L}])`, flags);

const PATTERNS = Object.freeze([
  { certain: true, re: word("chat\\s*-?\\s*gpt") },
  // GPT versionné uniquement : GPT-4, GPT-4o, GPT4o, GPT-3.5 (jamais « GPT 45 »).
  { certain: true, re: word("gpt(?:-\\d|\\d)(?:[.,]\\d+)?[a-z]?") },
  { certain: true, re: word("open\\s?ai") },
  { certain: true, re: word("en\\s+tant\\s+qu['’]\\s*(?:ia|intelligence\\s+artificielle|assistant(?:e)?)") },
  { certain: true, re: word("je\\s+suis\\s+une?\\s+(?:ia|intelligence\\s+artificielle|assistant(?:e)?\\s+virtuel(?:le)?)") },
  { certain: true, re: word("mod[eè]les?\\s+de\\s+langage|language\\s+model") },
  { certain: true, re: word("as\\s+an\\s+ai") },
  { certain: false, re: word("anthropic") },
  { certain: false, re: strictWord("claude(?:\\.ai|\\s+ai|\\s+d['’]\\s*anthropic|\\s+\\d(?:[.,]\\d+)?)") },
  // « IA » en majuscules seulement : « ia » apparaît dans des mots courants.
  { certain: false, re: word("I\\.?A\\.?", "u") },
  { certain: false, re: word("intelligence\\s+artificielle|artificial\\s+intelligence") },
  // Préambules de réponse générée.
  { certain: false, re: word("(?:bien\\s+s[uû]r|certainement|avec\\s+plaisir)\\s*[!,.]\\s*voici") },
  { certain: false, re: /(?<![\p{L}\p{N}])voici\s+(?:votre|ton|ta|tes|le|la|les)\s[^.\n]{0,80}?\bg[ée]n[ée]r[ée]e?s?(?![\p{L}\p{N}])/iu },
]);

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Retire le prénom et le nom de la cliente avant la lecture. */
function withoutIdentity(text, identityNames = []) {
  let out = text;
  for (const name of identityNames) {
    const clean = typeof name === "string" ? name.trim() : "";
    if (clean.length < 2) continue;
    out = out.replace(new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(clean)}(?![\\p{L}\\p{N}])`, "giu"), " ");
  }
  return out;
}

/** Mentions détaillées d'un texte : [{ text, certain }]. */
export function findAiMentionsDetailed(text, { identityNames = [] } = {}) {
  const value = typeof text === "string" ? withoutIdentity(text, identityNames) : "";
  if (!value) return [];
  const found = [];
  for (const { re, certain } of PATTERNS) {
    const match = value.match(re);
    if (match && !found.some((item) => item.text === match[0])) found.push({ text: match[0], certain });
  }
  return found;
}

/** Mentions trouvées dans un texte (dédoublonnées, dans l'ordre). */
export function findAiMentions(text, options) {
  return findAiMentionsDetailed(text, options).map((item) => item.text);
}

// Champs jamais lus : techniques (identifiants, empreintes, codes) ou
// porteurs de l'identité (salutation, prénom affiché).
const SKIPPED_KEYS = new Set([
  "id", "slot", "unit", "category", "foodKey", "food_key", "schema", "version",
  "status", "source_revision", "lot_id", "end_marker", "source_items_sha256",
  "content_sha256", "mode", "kind", "type", "locale", "email",
  "first_name", "greeting",
]);

function isSkippedKey(key) {
  return SKIPPED_KEYS.has(key) || /_id$|Id$|_ids$|sha256$/i.test(key);
}

function collectDetailed(value, key, options, out) {
  if (value == null) return;
  if (typeof value === "string") {
    if (!(key && isSkippedKey(key))) out.push(...findAiMentionsDetailed(value, options));
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectDetailed(item, key, options, out));
    return;
  }
  if (typeof value !== "object") return;
  for (const [childKey, item] of Object.entries(value)) {
    if (!isSkippedKey(childKey)) collectDetailed(item, childKey, options, out);
  }
}

/** Mentions de toutes les chaînes affichables : { certain: string[], possible: string[] }. */
export function findAiMentionsDeepDetailed(value, options = {}) {
  const out = [];
  collectDetailed(value, null, options, out);
  const certain = [...new Set(out.filter((item) => item.certain).map((item) => item.text))];
  const possible = [...new Set(out.filter((item) => !item.certain).map((item) => item.text))]
    .filter((text) => !certain.includes(text));
  return { certain, possible };
}

/** Toutes les mentions (certaines puis possibles). */
export function findAiMentionsDeep(value, options = {}) {
  const { certain, possible } = findAiMentionsDeepDetailed(value, options);
  return [...certain, ...possible];
}

/** Prénom et nom d'une cliente, à retirer avant la lecture. */
export function identityNamesOf(client) {
  return [client?.prenom, client?.nom, client?.form?.prenom, client?.form?.nom]
    .filter((name) => typeof name === "string" && name.trim().length >= 2);
}

export function aiMentionMessage(mentions, label = "") {
  return `${label ? `${label} : ` : ""}${AI_MENTION_MESSAGE} (« ${mentions.join(" », « ")} »).`;
}

/** Contenu nouveau (lot, recette) : bloqué à la première mention, certaine ou possible. */
export function assertNoAiMention(value, label = "", options = {}) {
  const mentions = findAiMentionsDeep(value, options);
  if (mentions.length > 0) {
    const error = new Error(aiMentionMessage(mentions, label));
    error.code = "ai_mention";
    error.mentions = mentions;
    throw error;
  }
  return true;
}

/** Instruction ajoutée aux prompts : jamais de mention de soi ni d'outil. */
export const NO_SELF_MENTION_INSTRUCTION = "Ne parle jamais de toi et ne nomme aucun outil, logiciel, assistant ou programme informatique, ni la façon dont ce texte a été rédigé. Écris comme Anissa, sans préambule (« Bien sûr », « Voici »).";
